import { useTranslation } from 'react-i18next';
import { currentLocale } from '../../i18n';
import React, { useState, useEffect } from 'react';
import { API_URL } from '../../utils/api';
import { fetchAvatarsBulk } from '../../utils/avatars';
import { fetchCountriesBulk } from '../../utils/countries';
import SteamAvatar from '../common/SteamAvatar';
import SteamFlag from '../common/SteamFlag';
import RankBadge from '../elo/RankBadge';
import './HeadToHead.css';

const HeadToHead = ({ steamA, steamB, onBack, onSelectPlayer, onSelectMatch }) => {
  const { t } = useTranslation();
  const [h2h, setH2h] = useState(null);
  const [playerA, setPlayerA] = useState(null);
  const [playerB, setPlayerB] = useState(null);
  const [eloA, setEloA] = useState(null);
  const [eloB, setEloB] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);

    Promise.all([
      fetch(`${API_URL}/api/elo/h2h/${steamA}/vs/${steamB}`).then(r => r.json()),
      fetch(`${API_URL}/api/player/${steamA}`).then(r => r.ok ? r.json() : null).catch(() => null),
      fetch(`${API_URL}/api/player/${steamB}`).then(r => r.ok ? r.json() : null).catch(() => null),
      fetch(`${API_URL}/api/elo/player/${steamA}`).then(r => r.json()).catch(() => null),
      fetch(`${API_URL}/api/elo/player/${steamB}`).then(r => r.json()).catch(() => null),
    ]).then(async ([h2hData, pA, pB, eA, eB]) => {
      if (h2hData.error === 'not_found') {
        setError(t('h2h.notFound'));
        setLoading(false);
        return;
      }
      const steamids = [steamA, steamB];
      await Promise.all([fetchAvatarsBulk(steamids), fetchCountriesBulk(steamids)]);
      setH2h(h2hData);
      setPlayerA(pA);
      setPlayerB(pB);
      setEloA(eA && !eA.error ? eA : null);
      setEloB(eB && !eB.error ? eB : null);
      setLoading(false);
    }).catch(() => {
      setError(t('settings.networkError'));
      setLoading(false);
    });
  }, [steamA, steamB]);

  if (loading) return <div className="loading-screen"><div className="loader"></div></div>;
  if (error) return (
    <div className="error-screen">
      <button className="back-button" onClick={onBack}>← {t('profile.back')}</button>
      <p>{error}</p>
    </div>
  );
  if (!h2h) return null;

  const { player_a, player_b, total_matches, player_a_wins, player_b_wins, matches } = h2h;
  const nameA = player_a.player_name;
  const nameB = player_b.player_name;

  const StatRow = ({ label, valA, valB, higherIsBetter = true, format }) => {
    const a = parseFloat(valA);
    const b = parseFloat(valB);
    const fmt = format || (v => v);
    let classA = '', classB = '';
    if (!isNaN(a) && !isNaN(b) && a !== b) {
      if (higherIsBetter) {
        classA = a > b ? 'stat-better' : 'stat-worse';
        classB = b > a ? 'stat-better' : 'stat-worse';
      } else {
        classA = a < b ? 'stat-better' : 'stat-worse';
        classB = b < a ? 'stat-better' : 'stat-worse';
      }
    }
    return (
      <div className="h2h-stat-row">
        <span className={`h2h-stat-val ${classA}`}>{fmt(valA)}</span>
        <span className="h2h-stat-label">{label}</span>
        <span className={`h2h-stat-val ${classB}`}>{fmt(valB)}</span>
      </div>
    );
  };

  const oA = playerA?.overview || {};
  const oB = playerB?.overview || {};

  return (
    <div className="h2h-page">
      <button className="back-button" onClick={onBack}>← {t('profile.back')}</button>

      <div className="h2h-header">
        <div
          className="h2h-player clickable"
          onClick={() => onSelectPlayer && player_a.steamid64 && onSelectPlayer(player_a.steamid64)}
        >
          <SteamAvatar steamid64={player_a.steamid64} name={nameA} size="large" />
          <h2><SteamFlag steamid64={player_a.steamid64} size="md" className="h2h-flag" />{nameA}</h2>
          {player_a.elo && <RankBadge elo={player_a.elo} rank={player_a.rank} size="medium" />}
          <span className="h2h-elo">{player_a.elo} ELO</span>
        </div>

        <div className="h2h-score">
          <span className="h2h-vs-label">VS</span>
          {total_matches > 0 && (
            <>
              <div className="h2h-record">
                <span className={`h2h-wins ${player_a_wins > player_b_wins ? 'leading' : ''}`}>{player_a_wins}</span>
                <span className="h2h-dash">—</span>
                <span className={`h2h-wins ${player_b_wins > player_a_wins ? 'leading' : ''}`}>{player_b_wins}</span>
              </div>
              <span className="h2h-total">{total_matches} matcher mot varandra</span>
            </>
          )}
        </div>

        <div
          className="h2h-player clickable"
          onClick={() => onSelectPlayer && player_b.steamid64 && onSelectPlayer(player_b.steamid64)}
        >
          <SteamAvatar steamid64={player_b.steamid64} name={nameB} size="large" />
          <h2><SteamFlag steamid64={player_b.steamid64} size="md" className="h2h-flag" />{nameB}</h2>
          {player_b.elo && <RankBadge elo={player_b.elo} rank={player_b.rank} size="medium" />}
          <span className="h2h-elo">{player_b.elo} ELO</span>
        </div>
      </div>

      {/* General stat comparison */}
      {(playerA || playerB) && (
        <div className="h2h-comparison">
          <h3>{t('h2h.statComparison')}</h3>
          <div className="h2h-stat-table">
            <div className="h2h-stat-header">
              <span>{nameA}</span>
              <span></span>
              <span>{nameB}</span>
            </div>
            {eloA && eloB && (
              <>
                <StatRow label="ELO" valA={eloA.elo} valB={eloB.elo} />
                <StatRow label="W/L" valA={`${eloA.wins}/${eloA.losses}`} valB={`${eloB.wins}/${eloB.losses}`} />
                <StatRow label="Win%" valA={eloA.win_rate} valB={eloB.win_rate} format={v => `${v}%`} />
                <StatRow label="Peak" valA={eloA.peak_elo} valB={eloB.peak_elo} />
              </>
            )}
            <StatRow label="K/D" valA={oA.kd_ratio} valB={oB.kd_ratio} />
            <StatRow label="ADR" valA={oA.avg_adr} valB={oB.avg_adr} />
            <StatRow label="HS%" valA={oA.headshot_percentage} valB={oB.headshot_percentage} format={v => `${v}%`} />
            <StatRow label={t('players.colMatches')} valA={oA.total_matches} valB={oB.total_matches} />
            <StatRow label="Kills" valA={oA.total_kills} valB={oB.total_kills} />
            <StatRow label="Deaths" valA={oA.total_deaths} valB={oB.total_deaths} higherIsBetter={false} />
          </div>
        </div>
      )}

      {/* H2H match history */}
      {total_matches > 0 && (
        <div className="h2h-matches">
          <h3>{t('h2h.meetings')} ({total_matches})</h3>
          <div className="h2h-match-list">
            {matches.map((match, idx) => {
              const aStats = match.player_a_stats || {};
              const bStats = match.player_b_stats || {};
              const aElo = match.player_a_elo || {};
              const bElo = match.player_b_elo || {};
              const aWon = match.winner === 'player_a';

              return (
                <div
                  key={match.match_id || idx}
                  className="h2h-match-row clickable"
                  onClick={() => onSelectMatch && onSelectMatch(match.match_id)}
                >
                  <span className="h2h-match-date">
                    {new Date(match.date).toLocaleDateString(currentLocale())}
                  </span>

                  <div className={`h2h-match-player ${aWon ? 'winner' : 'loser'}`}>
                    <span className="h2h-kda">{aStats.kills}/{aStats.deaths}/{aStats.assists}</span>
                    {aElo.elo_change != null && (
                      <span className={`h2h-elo-change ${aElo.elo_change >= 0 ? 'positive' : 'negative'}`}>
                        {aElo.elo_change >= 0 ? '+' : ''}{aElo.elo_change}
                      </span>
                    )}
                  </div>

                  <span className="h2h-vs">vs</span>

                  <div className={`h2h-match-player ${!aWon ? 'winner' : 'loser'}`}>
                    <span className="h2h-kda">{bStats.kills}/{bStats.deaths}/{bStats.assists}</span>
                    {bElo.elo_change != null && (
                      <span className={`h2h-elo-change ${bElo.elo_change >= 0 ? 'positive' : 'negative'}`}>
                        {bElo.elo_change >= 0 ? '+' : ''}{bElo.elo_change}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default HeadToHead;
